/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { Widget } from '../../models/WidgetModels';
import { useGetUsersQuery } from '../../store/api/KeyWorkOrderStatusesWidgetApi';
import {
    DropDownSelect,
    ButtonPrimary,
    ButtonSecondary,
    RadioGroup,
} from 'motili-ui';
import { snakeCaseToTitleCase } from '../../../utils/WidgetUtils';
import { DrawerHeadingAndTitle } from '../../components/drawer/DrawerHeadingAndTitle';
import { DrawerContainer } from '../../components/drawer/DrawerContainer';
import { ToggleButtonGroup } from '../../components/toggleButtonGroup/ToggleButtonGroup';
import useDrawerStyles from '../../components/drawer/DrawerStyles';
import {
    useGetExceptionFlagBucketsQuery,
    useGetExceptionFlagsQuery,
    useGetExceptionFlagTreeQuery,
} from 'widgets/store/api/ExceptionFlagWidgetApi';
import {WorkOrderUserType} from 'widgets/models/ExceptionFlagModel';
import {sortBy} from 'lodash';

interface CustomizeWidgetDrawerProps {
    widgetDetails: Widget;
    handleSubmit: (widgetDetails: Widget) => void;
    handleClose: () => void;
}
const MAX_MULTI = 5;
const norm = (s: string) => (s || '').trim().replace(/\s+/g, ' ').toLowerCase();
const buildAllowedReasonIds = (tree: any[] = [], flagIds: string[]) =>
    new Set(
        tree
            .filter(f => flagIds.includes(f.id))
            .flatMap(f => (f.reasons ?? []).map((r: any) => r.id))
    );

const buildAllowedSecondaryIds = (
    tree: any[] = [],
    reasonIds: string[],
    flagIds: string[]
) =>
    new Set(
        tree
            .flatMap(f => (f.reasons ?? []))
            .filter((r: any) => reasonIds.includes(r.id))
            .flatMap((r: any) => (r.secondaryReasons ?? []))
            .filter((s: any) => flagIds.includes(s.grandParentId))
            .map((s: any) => s.id)
    );

export function CustomizeWidgetDrawer(
    props: CustomizeWidgetDrawerProps
): JSX.Element {
    const styles = useDrawerStyles();
    const {
        widgetDetails: initialWidgetDetails,
        handleSubmit,
        handleClose,
    } = props;
    const [widgetData, setWidgetData] = useState(initialWidgetDetails);
    const [userOpen, setUserOpen] = useState<boolean>(
        widgetData.config.view === 'Other'
    );
    const [ownerErrorMessage, setOwnerErrorMessage] = useState<boolean>(false);
    const [maxOwnerErrorMessage, setMaxOwnerErrorMessage] = useState<boolean>(false);
    const [statusErrorMessage, setStatusErrorMessage] =
        useState<boolean>(false);
    const { data: flagTree } = useGetExceptionFlagTreeQuery();
    const { data: getWorkOrderExceptionFlags } =
        useGetExceptionFlagsQuery(undefined);
    const { data: getWorkOrderExceptionFlagBuckets } =
        useGetExceptionFlagBucketsQuery(undefined);
    const { data: getUserData } = useGetUsersQuery(null);

    const [workOrderExceptionFlagsDropdownOptions, setWorkOrderExceptionFlagsDropdownOptions] = useState<any[]>([]);

    useEffect(() => {
        if (getWorkOrderExceptionFlags && getWorkOrderExceptionFlags?.length > 0) {
            const _workOrderExceptionFlagsDropdownOptions =
                getWorkOrderExceptionFlags?.map(record => {
                    return {
                        label: record.description || snakeCaseToTitleCase(record.name),
                        value: record.name,
                    };
                }) || [];
            setWorkOrderExceptionFlagsDropdownOptions(sortBy(_workOrderExceptionFlagsDropdownOptions, (o) => o.label));
        }
    }, [getWorkOrderExceptionFlags]);

    const workOrderExceptionFlagBucketsDropdownOptions =
        getWorkOrderExceptionFlagBuckets
            ?.filter(
                record => record.name !== 'ERROR' && record.name !== 'FUTURE'
            )
            .map(record => {
                return {
                    label: record.description,
                    value: record.name,
                };
            }) || [];
    const userDropdownOptions: { label: string; value: string }[] =
        getUserData?.map(elem => {
            return {
                label: `${elem.firstName} ${elem.lastName}`,
                value: elem.id,
            };
        }) || [];
    const [maxFlagError, setMaxFlagError] = useState(false);
    const [maxReasonError, setMaxReasonError] = useState(false);
    const [maxSecondaryError, setMaxSecondaryError] = useState(false);

    const isMaxFlags       = (widgetData.config.exceptionFlags?.length ?? 0) >= MAX_MULTI;
    const isMaxReasons     = (widgetData.config.exceptionReasons?.length ?? 0) >= MAX_MULTI;
    const isMaxSecondary   = (widgetData.config.exceptionSecondaryReasons?.length ?? 0) >= MAX_MULTI;
    const selectedFlagIds = useMemo(
        () => (widgetData.config.exceptionFlags || []).map((f: any) => f.value),
        [widgetData.config.exceptionFlags]
    );
    const exceptionReasonOptions = useMemo(() => {
        if (!flagTree || selectedFlagIds.length === 0) return [];

        // Order the selected flags to decide “first one wins” (first-match wins).
        const flagOrder = new Map<string, number>();
        selectedFlagIds.forEach((id, idx) => flagOrder.set(id, idx));

        const map = new Map<string, { label: string; value: string; flagIdx: number }>();

        flagTree
            .filter((f: any) => selectedFlagIds.includes(f.id))
            .forEach((f: any) => {
                const fIdx = flagOrder.get(f.id) ?? 9999;
                (f.reasons ?? []).forEach((r: any) => {
                    const key = norm(r.display);
                    const prev = map.get(key);
                    if (!prev || fIdx < prev.flagIdx) {
                        map.set(key, { label: r.display, value: r.id, flagIdx: fIdx });
                    }
                });
            });

        return Array.from(map.values())
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ label, value }) => ({ label, value }));
    }, [flagTree, selectedFlagIds]);


    const selectedReasonIds = useMemo(
        () => (widgetData.config.exceptionReasons || []).map((r: any) => r.value),
        [widgetData.config.exceptionReasons]
    );

    const exceptionSecondaryReasonOptions = useMemo(() => {
        if (!flagTree || selectedReasonIds.length === 0) return [];

        const gpOrder = new Map<string, number>();
        selectedFlagIds.forEach((id, idx) => gpOrder.set(id, idx));

        const map = new Map<string, { label: string; value: string; gpIdx: number }>();

        flagTree.forEach((f: any) => {
            (f.reasons ?? []).forEach((r: any) => {
                if (!selectedReasonIds.includes(r.id)) return;
                (r.secondaryReasons ?? []).forEach((s: any) => {
                    if (!selectedFlagIds.includes(s.grandParentId)) return;
                    const key = norm(s.display);
                    const idx = gpOrder.get(s.grandParentId) ?? 9999;
                    const prev = map.get(key);
                    if (!prev || idx < prev.gpIdx) {
                        map.set(key, { label: s.display, value: s.id, gpIdx: idx });
                    }
                });
            });
        });

        return Array.from(map.values())
            .sort((a, b) => a.label.localeCompare(b.label))
            .map(({ label, value }) => ({ label, value }));
    }, [flagTree, selectedReasonIds, selectedFlagIds]);


    useEffect(() => {
        if (!flagTree) return;
        const flagIds = (widgetData.config.exceptionFlags ?? []).map((f: any) => f.value);
        const allowedReasonIds = buildAllowedReasonIds(flagTree, flagIds);

        const prevReasons = widgetData.config.exceptionReasons ?? [];
        const filteredReasons = prevReasons.filter((r: any) => allowedReasonIds.has(r.value));

        if (filteredReasons.length !== prevReasons.length) {
            const allowedSecondaryIds = buildAllowedSecondaryIds(
                flagTree,
                filteredReasons.map((r: any) => r.value),
                flagIds
            );
            const filteredSecondary = (widgetData.config.exceptionSecondaryReasons ?? [])
                .filter((s: any) => allowedSecondaryIds.has(s.value));

            setWidgetData(prev => ({
                ...prev,
                config: {
                    ...prev.config,
                    exceptionReasons: filteredReasons,
                    exceptionSecondaryReasons: filteredSecondary,
                },
            }));
        }
    }, [exceptionReasonOptions, flagTree, widgetData.config.exceptionFlags]);
    useEffect(() => {
        if (!flagTree) return;
        const flagIds = (widgetData.config.exceptionFlags ?? []).map((f: any) => f.value);
        const reasonIds = (widgetData.config.exceptionReasons ?? []).map((r: any) => r.value);
        const allowedSecondaryIds = buildAllowedSecondaryIds(flagTree, reasonIds, flagIds);

        const prevSecondary = widgetData.config.exceptionSecondaryReasons ?? [];
        const filteredSecondary = prevSecondary.filter((s: any) => allowedSecondaryIds.has(s.value));

        if (filteredSecondary.length !== prevSecondary.length) {
            setWidgetData(prev => ({
                ...prev,
                config: {
                    ...prev.config,
                    exceptionSecondaryReasons: filteredSecondary,
                },
            }));
        }
    }, [exceptionSecondaryReasonOptions, flagTree, widgetData.config.exceptionReasons, widgetData.config.exceptionFlags]);



// ---------- Validations / cascade clear ----------
    // limit flags to 5 + clear dependent
    const workOrderExceptionOnChange = (selected: any[]): void => {
        if (selected.length > MAX_MULTI) { setMaxFlagError(true); return; }
        setMaxFlagError(false);

        if (!flagTree) {
            setWidgetData(prev => ({
                ...prev,
                config: { ...prev.config, exceptionFlags: selected }
            }));
            return;
        }

        setWidgetData(prev => {
            const nextFlagIds = selected.map(x => x.value);
            const allowedReasonIds = buildAllowedReasonIds(flagTree, nextFlagIds);
            const keptReasons = (prev.config.exceptionReasons ?? []).filter((r: any) =>
                allowedReasonIds.has(r.value)
            );
            const allowedSecondaryIds = buildAllowedSecondaryIds(
                flagTree,
                keptReasons.map((r: any) => r.value),
                nextFlagIds
            );
            const keptSecondary = (prev.config.exceptionSecondaryReasons ?? []).filter(
                (s: any) => allowedSecondaryIds.has(s.value)
            );

            return {
                ...prev,
                config: {
                    ...prev.config,
                    exceptionFlags: selected,
                    exceptionReasons: keptReasons,
                    exceptionSecondaryReasons: keptSecondary,
                },
            };
        });
    };


    // limit reasons to 5 + clear secondary
    const exceptionReasonsOnChange = (reasons: any[]): void => {
        if (Array.isArray(reasons) && reasons.length > MAX_MULTI) { setMaxReasonError(true); return; }
        setMaxReasonError(false);

        if (!flagTree) {
            setWidgetData(prev => ({
                ...prev,
                config: { ...prev.config, exceptionReasons: reasons }
            }));
            return;
        }

        setWidgetData(prev => {
            const nextReasonIds = reasons.map(r => r.value);
            const selectedFlagIdsNow = (prev.config.exceptionFlags ?? []).map(
                (f: any) => f.value
            );
            const allowedSecondaryIds = buildAllowedSecondaryIds(
                flagTree,
                nextReasonIds,
                selectedFlagIdsNow
            );
            const keptSecondary = (prev.config.exceptionSecondaryReasons ?? []).filter(
                (s: any) => allowedSecondaryIds.has(s.value)
            );

            return {
                ...prev,
                config: {
                    ...prev.config,
                    exceptionReasons: reasons,
                    exceptionSecondaryReasons: keptSecondary,
                },
            };
        });
    };

    // limit secondary to 5
    const exceptionSecondaryReasonsOnChange = (secondary: any[]): void => {
        if (Array.isArray(secondary) && secondary.length > MAX_MULTI) { setMaxSecondaryError(true); return; }
        setMaxSecondaryError(false);
        setWidgetData(prev => ({
            ...prev,
            config: { ...prev.config, exceptionSecondaryReasons: secondary },
        }));
    };

    useEffect(() => {
        if (!widgetData.config.exceptionReasons?.length) return;

        const allowedReasonIds = new Set(exceptionReasonOptions.map(o => o.value));
        const filteredReasons = widgetData.config.exceptionReasons.filter((r: any) => allowedReasonIds.has(r.value));

        if (filteredReasons.length !== widgetData.config.exceptionReasons.length) {
            setWidgetData(prev => ({
                ...prev,
                config: {
                    ...prev.config,
                    exceptionReasons: filteredReasons,
                    exceptionSecondaryReasons: [],
                },
            }));
        }
    }, [exceptionReasonOptions]);

    useEffect(() => {
        if (!widgetData.config.exceptionSecondaryReasons?.length) return;

        const allowedSecondaryIds = new Set(exceptionSecondaryReasonOptions.map(o => o.value));
        const filteredSecondary = widgetData.config.exceptionSecondaryReasons.filter((s: any) => allowedSecondaryIds.has(s.value));

        if (filteredSecondary.length !== widgetData.config.exceptionSecondaryReasons.length) {
            setWidgetData(prev => ({
                ...prev,
                config: {
                    ...prev.config,
                    exceptionSecondaryReasons: filteredSecondary,
                },
            }));
        }
    }, [exceptionSecondaryReasonOptions]);


    useEffect((): void => {
        if (initialWidgetDetails?.config.exceptionFlags && initialWidgetDetails?.config.exceptionFlags.length > 0 &&
            getWorkOrderExceptionFlags && getWorkOrderExceptionFlags?.length > 0) {
            initialWidgetDetails.config.exceptionFlags = initialWidgetDetails.config.exceptionFlags.map(flag => {
                const _woef = getWorkOrderExceptionFlags.find(woef => woef.name === flag.value);
                return _woef ? {label: _woef.description, value: _woef.name} : flag;
            })
        }
        setWidgetData(initialWidgetDetails);
    }, [initialWidgetDetails, getWorkOrderExceptionFlags]);

    useEffect((): void => {
        setStatusErrorMessage(widgetData.config?.exceptionFlags?.length === 0);
        if (
            !widgetData.config.exceptionFlags ||
            widgetData.config.exceptionFlags.length < 5
        ) {
            // setMaxStatusErrorMessage(false);
        }
    }, [widgetData.config?.exceptionFlags]);

    useEffect((): void => {
        setOwnerErrorMessage(widgetData.config?.user?.length === 0);
        if (!widgetData.config.user || widgetData.config.user.length < 5) {
            setMaxOwnerErrorMessage(false);
        }
    }, [widgetData.config?.user]);

    useEffect((): void => {
        const clearUserDropdown = (): void => {
            setUserOpen(widgetData.config.view === 'Other');

            if (widgetData.config.view !== 'Other') {
                setWidgetData(prevWidgetData => ({
                    ...prevWidgetData,
                    config: {
                        ...prevWidgetData.config,
                        user: [],
                    },
                }));
            }
        };
        clearUserDropdown();
    }, [widgetData.config.view, setWidgetData]);

    useEffect(() => {
        if (widgetData.config.view === 'Just Me') {
            setWidgetData({
                ...widgetData,
                config: { ...widgetData.config, chartType: 'Pie Chart' },
            });
        } else if (widgetData.config.view === 'All') {
            setWidgetData({
                ...widgetData,
                config: {
                    ...widgetData.config,
                    chartType: 'Pie Chart',
                },
            });
        } else {
            setWidgetData({
                ...widgetData,
                config: { ...widgetData.config, chartType: 'Bar Graph' },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [widgetData?.config?.view, widgetData?.config?.workOrderUserType]);




    const handleNoPerPageButtonSelect = (event): void => {
        setWidgetData({
            ...widgetData,
            config: {
                ...widgetData?.config,
                noOfDocumentsToShow: Number(event.target.value),
            },
        });
    };
    const handleUserButtonSelect = (event): void => {
        if (event.target.value === 'Other') {
            setUserOpen(true);
        } else {
            setUserOpen(false);
        }
        setWidgetData({
            ...widgetData,
            config: {
                ...widgetData?.config,
                view: event.target.value,
                user: [],
            },
        });
    };
    const handleDropdownSelect = (type: string, value: []): void => {
        setWidgetData({
            ...widgetData,
            config: {
                ...widgetData?.config,
                [type]: value,
            },
        });
    };

    const userOnChange = (userIds: []): void => {
        if (userIds.length > MAX_MULTI) {
            setMaxOwnerErrorMessage(true);
        } else {
            setMaxOwnerErrorMessage(false);
            handleDropdownSelect('user', userIds);
        }
    };


    const updateDocumentTypeFilter = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setWidgetData({
            ...widgetData,
            config: {
                ...widgetData?.config,
                workOrderUserType: (event.target as HTMLInputElement)
                    .value as WorkOrderUserType,
            },
        });
    };

    const submitWithIds = (): void => {
        const normalized: Widget = {
            ...widgetData,
            config: {
                ...widgetData.config,
                exceptionReasonIds:
                    (widgetData.config.exceptionReasons ?? []).map((r: any) => r.value),
                exceptionSecondaryReasonIds:
                    (widgetData.config.exceptionSecondaryReasons ?? []).map((s: any) => s.value),
                exceptionReasons: widgetData.config.exceptionReasons ?? [],
                exceptionSecondaryReasons: widgetData.config.exceptionSecondaryReasons ?? [],
            },
        };

        handleSubmit(normalized);
    };



    return (
        <DrawerContainer handleClose={handleClose}>
            <>
                <DrawerHeadingAndTitle
                    widgetData={widgetData}
                    setWidgetData={setWidgetData}
                    dataTestId={'EFWidgetTitleInput'}
                />
                <div className={styles.filterContainer}>
                    <div>
                        <div className={styles.dataVisualTypeRow}>
                            <h4>Select the Number of Records Per Page</h4>
                        </div>
                    </div>
                    <div>
                        <ToggleButtonGroup
                            options={[
                                {
                                    id: 'fiveButton',
                                    text: '5',
                                    value: 5,
                                    dataTestId: 'EFWidgetFivePerPageButton',
                                },
                                {
                                    id: 'tenButton',
                                    text: '10',
                                    value: 10,
                                    dataTestId: 'EFWidgetTenPerPageButton',
                                },
                            ]}
                            type={'noOfDocumentsToShow'}
                            selectedOption={
                                widgetData.config.noOfDocumentsToShow
                            }
                            handleButtonSelect={handleNoPerPageButtonSelect}
                        />
                    </div>
                    <div className={styles.marginTop20}>
                        <h4>Filter</h4>
                    </div>
                    <div className={styles.multipleInputRow}>
                        <div className={styles.dropdownSelectWidth}>
                            <DropDownSelect
                                data-test-id={
                                    'EFWidgetWorkOrderExceptionDropdown'
                                }
                                required={true}
                                isMulti={true}
                                onChange={exceptionFlag => {
                                    workOrderExceptionOnChange(exceptionFlag);
                                }}
                                name={'work-order-exception-select'}
                                label={'Exception Flag Type (Limit up to 5)'}
                                options={workOrderExceptionFlagsDropdownOptions}
                                value={widgetData.config.exceptionFlags}
                            />
                            {statusErrorMessage && (
                                <div className={styles.errorMessageTextColor}>
                                    Exception Flag Type is required
                                </div>
                            )}
                            {maxFlagError && (
                                <div className={styles.errorMessageTextColor}>
                                    Exception limit reached
                                </div>
                            )}
                        </div>
                        {/*/!*date range type & date range from*!/*/}
                        <div style={{ width: '263px' }}>
                            <DropDownSelect
                                data-test-id={
                                    'EFWidgetExceptionFlagBucketDropdown'
                                }
                                required={true}
                                isMulti={false}
                                onChange={option => {
                                    handleDropdownSelect(
                                        'exceptionFlagBucket',
                                        option
                                    );
                                }}
                                name={'exception-flag-bucket-select'}
                                label={'Days Since Exception Flag Placed'}
                                options={
                                    workOrderExceptionFlagBucketsDropdownOptions
                                }
                                value={widgetData.config.exceptionFlagBucket}
                            />
                        </div>
                    </div>
                    {/* Exception Reason */}
                    {exceptionReasonOptions.length > 0 && (
                        <div className={styles.singleDropdownSelectRow}>
                            <DropDownSelect
                                data-test-id={'EFWidgetExceptionReasonDropdown'}
                                required={false}
                                isMulti={true}
                                onChange={(reasons) => exceptionReasonsOnChange(reasons)}
                                name={'exception-reason-select'}
                                label={'Exception Reason'}
                                options={exceptionReasonOptions}
                                value={widgetData.config.exceptionReasons || []}
                            />
                            {maxReasonError && (
                                <div className={styles.errorMessageTextColor}>Exception Reason limit reached</div>
                            )}
                        </div>
                    )}

                    {/* Exception Secondary Reason */}
                    {exceptionSecondaryReasonOptions.length > 0 && (
                        <div className={styles.singleDropdownSelectRow}>
                            <DropDownSelect
                                data-test-id={'EFWidgetExceptionSecondaryReasonDropdown'}
                                required={false}
                                isMulti={true}
                                onChange={(value) => exceptionSecondaryReasonsOnChange(value)}
                                name={'exception-secondary-reason-select'}
                                label={'Exception Secondary Reason'}
                                options={exceptionSecondaryReasonOptions}
                                value={widgetData.config.exceptionSecondaryReasons || []}
                            />
                            {maxSecondaryError && (
                                <div className={styles.errorMessageTextColor}>Secondary Reason limit reached</div>
                            )}
                        </div>
                    )}
                    <div>
                        <RadioGroup
                            options={[
                                {
                                    label: 'Owner View',
                                    value: WorkOrderUserType.OWNER,
                                    id: WorkOrderUserType.OWNER,
                                },
                                {
                                    label: 'Co-Owner View',
                                    value: WorkOrderUserType.CO_OWNER,
                                    id: WorkOrderUserType.CO_OWNER
                                },
                                {
                                    label: 'Approval Owner View',
                                    value: WorkOrderUserType.APPROVAL_OWNER,
                                    id: WorkOrderUserType.APPROVAL_OWNER,
                                },
                            ].map(opt => ({
                                ...opt,
                                checked:
                                    widgetData?.config?.workOrderUserType ===
                                    opt.value,
                            }))}
                            name={'filter-select'}
                            label={''}
                            onChange={updateDocumentTypeFilter}
                            className={'document-type-select'}
                            required
                            data-test-id={'CDEWidgetRadios'}
                        />
                    </div>
                    <div>
                        <ToggleButtonGroup
                            options={[
                                {
                                    id: 'JustMeButton',
                                    text: 'Just Me',
                                    value: 'Just Me',
                                    dataTestId: 'EFWidgetJustMeButton',
                                },
                                {
                                    id: 'OtherButton',
                                    text: 'Other',
                                    value: 'Other',
                                    dataTestId: 'EFWidgetOtherButton',
                                },
                                {
                                    id: 'AllViewButton',
                                    text: 'All',
                                    value: 'All',
                                    dataTestId: 'EFWidgetViewAllButton',
                                },
                            ]}
                            type={'view'}
                            selectedOption={widgetData.config.view}
                            handleButtonSelect={handleUserButtonSelect}
                        />
                    </div>
                    {userOpen && (
                        <div className={styles.singleDropdownSelectRow}>
                            <DropDownSelect
                                data-test-id={'EFWidgetUserDropdown'}
                                required={true}
                                isMulti={true}
                                onChange={value => {
                                    userOnChange(value);
                                }}
                                name={'user-select'}
                                label={
                                    widgetData.config.workOrderUserType === 'OWNER'
                                        ? 'Select Owner (Limit up to 5)'
                                        : widgetData.config.workOrderUserType === 'CO_OWNER'
                                            ? 'Select Co-Owner (Limit up to 5)'
                                            : 'Select Approval Owner (Limit up to 5)'
                                }

                                options={userDropdownOptions}
                                value={widgetData.config.user}
                            />
                            {ownerErrorMessage && (
                                <div className={styles.errorMessageTextColor}>
                                    At least one owner is required
                                </div>
                            )}
                            {maxOwnerErrorMessage && (
                                <div className={styles.errorMessageTextColor}>
                                    Owner limit reached
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.confirmCancelContainer}>
                    <div className={styles.cancelButtonRightPadding}>
                        <ButtonSecondary
                            data-test-id={'EFWidgetDrawerCancelButton'}
                            onClick={handleClose}
                        >
                            Cancel
                        </ButtonSecondary>
                    </div>
                    <div>
                        <ButtonPrimary
                            data-test-id={'EFWidgetDrawerConfirmButton'}
                            disabled={
                                statusErrorMessage ||
                                (ownerErrorMessage &&
                                    widgetData.config.view === 'Other') ||
                                (userOpen &&
                                    widgetData.config.user.length === 0) ||
                                widgetData.config.workOrderUserType === null ||
                                widgetData.config.name.length === 0
                            }
                            onClick={submitWithIds}
                        >
                            Confirm
                        </ButtonPrimary>
                    </div>
                </div>
            </>
        </DrawerContainer>
    );
}
